import { combineReducers, createReducer, on } from '@ngrx/store';
import { ScheduleEditorProgress } from 'app/admin/schedules/schedules-editor/incremental-progress-schema';
import { keyBy, omitBy } from 'lodash';
import * as moment from 'moment';
import * as ScheduleEditorActions from './schedule-editor.actions';

export const schedulesEditorFeatureKey = 'scheduleEditor';

type StatusState<Success extends object, Error = { error: unknown }> =
	| { status: 'loading' }
	| ({ status: 'loaded'; loadedAt: Date } & Success)
	| ({ status: 'error' } & Error);

type ScheduleEditorProgressById = Record<ScheduleEditorProgress['id'], ScheduleEditorProgress>;
type BaseState = { status: 'unloaded' } | StatusState<{ byId: ScheduleEditorProgressById }>;

type CreateState = { create: Record<string, StatusState<Pick<ScheduleEditorProgress, 'id'>>> };

type UpdateState = { update: Record<string, StatusState<Pick<ScheduleEditorProgress, 'id'>>> };

export type IncrementalProgressState = BaseState & CreateState & UpdateState;

const incrementalProgressInitialState = { status: 'unloaded' as const, create: {}, update: {} };

const StaleThresholdMs = 10_000;
const MaxStaleCount = 100;
const dropStaleEntries = <Success extends object, Error = { error: unknown }>(state: Record<string, StatusState<Success, Error>>) => {
	return Object.keys(state).length < MaxStaleCount
		? state
		: omitBy(state, (state) => state.status === 'loaded' && moment().isAfter(moment(state.loadedAt).add(StaleThresholdMs, 'ms')));
};

const incrementalProgressReducer = createReducer<IncrementalProgressState>(
	incrementalProgressInitialState,

	on(ScheduleEditorActions.loadIncrementalProgresses, ({ create, update }) => ({ create, update, status: 'loading' })),
	on(ScheduleEditorActions.loadIncrementalProgressesSuccess, ({ create, update }, { progresses }) => ({
		create,
		update,
		status: 'loaded',
		loadedAt: new Date(),
		byId: keyBy(progresses, 'id'),
	})),
	on(ScheduleEditorActions.loadIncrementalProgressesFailure, ({ create, update }, { error }) => ({ create, update, status: 'error', error })),

	on(ScheduleEditorActions.createIncrementalProgress, (state, { correlationId }) => {
		return {
			...state,
			create: {
				...state.create,
				[correlationId]: {
					status: 'loading',
				},
			},
		};
	}),
	on(ScheduleEditorActions.createIncrementalProgressSuccess, (state, { correlationId, progress }) => {
		const create = { ...state.create, [correlationId]: { status: 'loaded' as const, loadedAt: new Date(), id: progress.id } };
		return state.status === 'loaded'
			? {
					...state,
					create,
					byId: { ...state.byId, [progress.id]: progress },
			  }
			: { ...state, create };
	}),
	on(ScheduleEditorActions.createIncrementalProgressFailure, (state, { correlationId, error }) => {
		return { ...state, create: { ...state.create, [correlationId]: { status: 'error' as const, error } } };
	}),

	on(ScheduleEditorActions.updateIncrementalProgress, (state, { correlationId }) => {
		return {
			...state,
			update: {
				...dropStaleEntries(state.update),
				[correlationId]: {
					status: 'loading',
				},
			},
		};
	}),
	on(ScheduleEditorActions.updateIncrementalProgressSuccess, (state, { correlationId, progress }) => {
		const update = { ...dropStaleEntries(state.update), [correlationId]: { status: 'loaded' as const, loadedAt: new Date(), id: progress.id } };
		return state.status === 'loaded'
			? {
					...state,
					update,
					byId: { ...state.byId, [progress.id]: progress },
			  }
			: { ...state, update };
	}),
	on(ScheduleEditorActions.updateIncrementalProgressFailure, (state, { correlationId, error }) => {
		return { ...state, update: { ...dropStaleEntries(state.update), [correlationId]: { status: 'error' as const, error } } };
	})
);

export interface State {
	incrementalProgress: IncrementalProgressState;
}

export const reducer = combineReducers({
	incrementalProgress: incrementalProgressReducer,
});
